/**
 * ProjectsPage Component
 *
 * Lists all Flux DC projects with create/delete actions.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { vortxColors, vortxTypography } from '@vortx/shared-ui';
import { useAppProjects } from '../hooks/useAppProjects';
import { createProject, deleteProject } from '../services/appService';
import { appConfig } from '../config/appConfig';

function ProjectsPage() {
  const navigate = useNavigate();
  const { projects, loading, error, refresh } = useAppProjects();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [creating, setCreating] = useState(false);

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;

    setCreating(true);
    try {
      const project = await createProject({
        name: newProjectName,
        app_key: appConfig.appKey,
      });
      setCreateDialogOpen(false);
      setNewProjectName('');
      navigate(`${appConfig.appPath}/${project.id}`);
    } catch (err) {
      console.error('Failed to create DC project:', err);
      alert('Failed to create project: ' + err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteProject = async (projectId, projectName) => {
    if (!window.confirm(`Delete project "${projectName}"?`)) return;

    try {
      await deleteProject(projectId);
      refresh();
    } catch (err) {
      console.error('Failed to delete DC project:', err);
      alert('Failed to delete project: ' + err.message);
    }
  };

  if (loading && !projects.length) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: vortxColors.gold }} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontFamily: vortxTypography.heading,
            fontWeight: 700,
            color: vortxColors.white,
          }}
        >
          {appConfig.appName} Projects
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{
            background: `linear-gradient(135deg, ${vortxColors.gold} 0%, #d4a574 100%)`,
            color: '#000',
            fontWeight: 600,
            '&:hover': {
              background: `linear-gradient(135deg, #d4a574 0%, ${vortxColors.gold} 100%)`,
            },
          }}
        >
          New Project
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {projects.length === 0 ? (
        <Card
          sx={{
            bgcolor: vortxColors.charcoal,
            border: `1px solid ${vortxColors.teal}`,
            textAlign: 'center',
            py: 8,
          }}
        >
          <CardContent>
            <Typography variant="h6" sx={{ color: vortxColors.stone, mb: 2 }}>
              No projects yet
            </Typography>
            <Typography sx={{ color: vortxColors.stone, mb: 3 }}>
              Create your first data center project to get started.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              sx={{
                borderColor: vortxColors.gold,
                color: vortxColors.gold,
                '&:hover': {
                  borderColor: '#d4a574',
                  bgcolor: 'rgba(178, 140, 80, 0.1)',
                },
              }}
            >
              Create Project
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {projects.map((project) => (
            <Grid item xs={12} sm={6} md={4} key={project.id}>
              <Card
                sx={{
                  bgcolor: vortxColors.charcoal,
                  border: `1px solid ${vortxColors.teal}`,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'all 0.3s',
                  '&:hover': {
                    borderColor: vortxColors.gold,
                    boxShadow: `0 0 20px rgba(178, 140, 80, 0.3)`,
                  },
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontFamily: vortxTypography.heading,
                      color: vortxColors.white,
                      mb: 1,
                    }}
                  >
                    {project.name}
                  </Typography>
                  {project.site_name && (
                    <Typography variant="body2" sx={{ color: vortxColors.stone }}>
                      {project.site_name}
                    </Typography>
                  )}
                  {project.updated_at && (
                    <Typography variant="caption" sx={{ color: vortxColors.stone, display: 'block', mt: 1 }}>
                      Updated {new Date(project.updated_at).toLocaleDateString()}
                    </Typography>
                  )}
                </CardContent>
                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => navigate(`${appConfig.appPath}/${project.id}`)}
                    sx={{
                      color: vortxColors.gold,
                      '&:hover': {
                        bgcolor: 'rgba(178, 140, 80, 0.1)',
                      },
                    }}
                  >
                    Edit
                  </Button>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteProject(project.id, project.name)}
                    sx={{
                      color: vortxColors.stone,
                      '&:hover': {
                        color: '#ff6b6b',
                        bgcolor: 'rgba(255, 107, 107, 0.1)',
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Project Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => !creating && setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: vortxColors.charcoal, color: vortxColors.white, fontFamily: vortxTypography.heading }}>
          Create New DC Project
        </DialogTitle>
        <DialogContent sx={{ bgcolor: vortxColors.charcoal, pt: 3 }}>
          <TextField
            autoFocus
            fullWidth
            label="Project Name"
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleCreateProject()}
            disabled={creating}
            sx={{
              '& .MuiOutlinedInput-root': {
                color: vortxColors.white,
                '& fieldset': { borderColor: vortxColors.teal },
                '&:hover fieldset': { borderColor: vortxColors.gold },
                '&.Mui-focused fieldset': { borderColor: vortxColors.gold, borderWidth: 2 },
              },
              '& .MuiInputLabel-root': {
                color: vortxColors.stone,
                '&.Mui-focused': { color: vortxColors.gold },
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ bgcolor: vortxColors.charcoal, p: 2 }}>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={creating} sx={{ color: vortxColors.stone }}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateProject}
            disabled={!newProjectName.trim() || creating}
            variant="contained"
            sx={{
              background: `linear-gradient(135deg, ${vortxColors.gold} 0%, #d4a574 100%)`,
              color: '#000',
              fontWeight: 600,
              '&:hover': {
                background: `linear-gradient(135deg, #d4a574 0%, ${vortxColors.gold} 100%)`,
              },
            }}
          >
            {creating ? <CircularProgress size={24} sx={{ color: '#000' }} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default ProjectsPage;
